import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { assertAttendanceOk, handleAttendanceApiError } from "./attendanceApi";

const { AttendanceAgent, AttendanceDeviceAdmin } = config.REST_API;

const unwrapEnvelope = (apiData) => apiData?.data ?? apiData?.Data ?? apiData;

const asArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const nested = raw.items ?? raw.Items ?? raw.results ?? raw.Results;
    if (Array.isArray(nested)) return nested;
  }
  return [];
};

const readApiBool = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }
  return defaultValue;
};

/** @param {unknown} raw */
export const normalizeAgentInstallation = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(o.id ?? o.Id ?? ""),
    name: String(o.name ?? o.Name ?? ""),
    clientInstanceId: String(o.clientInstanceId ?? o.ClientInstanceId ?? ""),
    lastSeenAt: o.lastSeenAt ?? o.LastSeenAt ?? null,
    agentVersion: String(o.agentVersion ?? o.AgentVersion ?? ""),
    isRevoked: readApiBool(o.isRevoked ?? o.IsRevoked, false),
    createdDate: o.createdDate ?? o.CreatedDate ?? null,
  };
};

/** @param {unknown} raw */
export const normalizeDeviceMapping = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(o.id ?? o.Id ?? ""),
    companyId: String(o.companyId ?? o.CompanyId ?? ""),
    deviceId: (() => {
      const v = o.deviceId ?? o.DeviceId;
      return v != null && String(v).trim() !== "" ? String(v) : null;
    })(),
    enrollmentNo: String(o.enrollmentNo ?? o.EnrollmentNo ?? ""),
    userId: String(o.userId ?? o.UserId ?? ""),
    userName: String(o.userName ?? o.UserName ?? ""),
  };
};

/** @param {unknown} raw */
export const normalizeUnmappedPunch = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(o.id ?? o.Id ?? ""),
    enrollmentNo: String(o.enrollmentNo ?? o.EnrollmentNo ?? ""),
    externalDeviceKey: String(o.externalDeviceKey ?? o.ExternalDeviceKey ?? ""),
    deviceId: (() => {
      const v = o.deviceId ?? o.DeviceId;
      return v != null && String(v).trim() !== "" ? String(v) : null;
    })(),
    punchTimeUtc: o.punchTimeUtc ?? o.PunchTimeUtc ?? null,
    inOutHint: Number(o.inOutHint ?? o.InOutHint ?? 0),
    processError: (() => {
      const v = o.processError ?? o.ProcessError;
      return v != null && String(v).trim() !== "" ? String(v) : "";
    })(),
  };
};

/** @param {unknown} raw */
export const normalizeMappedPunch = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const eventTypeRaw = o.eventType ?? o.EventType;
  const eventType = eventTypeRaw === null || eventTypeRaw === undefined || eventTypeRaw === ""
    ? null
    : Number(eventTypeRaw);
  return {
    id: String(o.id ?? o.Id ?? ""),
    enrollmentNo: String(o.enrollmentNo ?? o.EnrollmentNo ?? ""),
    externalDeviceKey: String(o.externalDeviceKey ?? o.ExternalDeviceKey ?? ""),
    deviceId: (() => {
      const v = o.deviceId ?? o.DeviceId;
      return v != null && String(v).trim() !== "" ? String(v) : null;
    })(),
    punchTimeUtc: o.punchTimeUtc ?? o.PunchTimeUtc ?? null,
    inOutHint: Number(o.inOutHint ?? o.InOutHint ?? 0),
    processStatus: Number(o.processStatus ?? o.ProcessStatus ?? 0) || 0,
    processError: (() => {
      const v = o.processError ?? o.ProcessError;
      return v != null && String(v).trim() !== "" ? String(v) : "";
    })(),
    mappedUserId: String(o.mappedUserId ?? o.MappedUserId ?? ""),
    userName: String(o.userName ?? o.UserName ?? ""),
    appliedEventId: String(o.appliedEventId ?? o.AppliedEventId ?? ""),
    eventType: Number.isFinite(eventType) ? eventType : null,
  };
};

/** @param {unknown} raw */
export const normalizeAttendanceDevice = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    id: String(o.id ?? o.Id ?? ""),
    name: String(o.name ?? o.Name ?? ""),
    vendor: String(o.vendor ?? o.Vendor ?? ""),
    externalDeviceKey: String(o.externalDeviceKey ?? o.ExternalDeviceKey ?? ""),
    officeId: Number(o.officeId ?? o.OfficeId ?? 0) || 0,
    officeName: String(o.officeName ?? o.OfficeName ?? ""),
    ipAddress: String(o.ipAddress ?? o.IpAddress ?? ""),
    port: (() => {
      const n = Number(o.port ?? o.Port);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    lastSyncAt: o.lastSyncAt ?? o.LastSyncAt ?? null,
    lastHeartbeatAt: o.lastHeartbeatAt ?? o.LastHeartbeatAt ?? null,
    isReachable: readApiBool(o.isReachable ?? o.IsReachable, false),
    isOnline: readApiBool(o.isOnline ?? o.IsOnline, false),
    lastError: (() => {
      const v = o.lastError ?? o.LastError;
      return v != null && String(v).trim() !== "" ? String(v) : "";
    })(),
    isActive: readApiBool(o.isActive ?? o.IsActive, true),
  };
};

export const bindAttendanceAgent = async ({ clientInstanceId, agentName, codeChallenge, redirectUri }) => {
  const res = await axiosBasic.post(AttendanceAgent.Bind, {
    ClientInstanceId: clientInstanceId,
    AgentName: agentName || null,
    CodeChallenge: codeChallenge,
    RedirectUri: redirectUri || null,
  });
  assertAttendanceOk(res.data);
  const d = unwrapEnvelope(res.data);
  const o = d && typeof d === "object" ? /** @type {Record<string, unknown>} */ (d) : {};
  return {
    installationId: String(o.installationId ?? o.InstallationId ?? ""),
    bindCode: String(o.bindCode ?? o.BindCode ?? ""),
    expiresAtUtc: o.expiresAtUtc ?? o.ExpiresAtUtc ?? null,
    companyId: String(o.companyId ?? o.CompanyId ?? ""),
    companyName: String(o.companyName ?? o.CompanyName ?? ""),
  };
};

export const fetchAgentInstallations = async () => {
  const res = await axiosBasic.get(AttendanceAgent.Installations);
  assertAttendanceOk(res.data);
  return asArray(unwrapEnvelope(res.data)).map(normalizeAgentInstallation).filter(Boolean);
};

export const revokeAgentInstallation = async (installationId) => {
  const id = encodeURIComponent(String(installationId || "").trim());
  if (!id) throw new Error("Installation id is required");
  const res = await axiosBasic.post(`${AttendanceAgent.RevokeBase}${id}`);
  assertAttendanceOk(res.data);
};

export const fetchAttendanceDevices = async () => {
  const res = await axiosBasic.get(AttendanceDeviceAdmin.Devices);
  assertAttendanceOk(res.data);
  return asArray(unwrapEnvelope(res.data)).map(normalizeAttendanceDevice).filter(Boolean);
};

export const fetchDeviceMappings = async () => {
  const res = await axiosBasic.get(AttendanceDeviceAdmin.Mappings);
  assertAttendanceOk(res.data);
  return asArray(unwrapEnvelope(res.data)).map(normalizeDeviceMapping).filter(Boolean);
};

const MAPPING_ROSTER_PAGE_SIZE = 50;
const CUSTOM_ENROLL = "__custom__";

const readStringList = (raw) =>
  asArray(raw)
    .map((v) => String(v ?? "").trim())
    .filter(Boolean);

/** @param {unknown} raw */
export const normalizeMappingRosterRow = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    userId: String(o.userId ?? o.UserId ?? ""),
    userName: String(o.userName ?? o.UserName ?? ""),
    email: String(o.email ?? o.Email ?? ""),
    imageUrl: String(o.imageUrl ?? o.ImageUrl ?? o.ImageURL ?? "").trim(),
    designation: String(o.designation ?? o.Designation ?? ""),
    departmentName: String(o.departmentName ?? o.DepartmentName ?? ""),
    employeeCode: String(o.employeeCode ?? o.EmployeeCode ?? ""),
    officeId: Number(o.officeId ?? o.OfficeId ?? 0) || 0,
    officeName: String(o.officeName ?? o.OfficeName ?? ""),
    isMapped: readApiBool(o.isMapped ?? o.IsMapped, false),
    mappingId: (() => {
      const v = o.mappingId ?? o.MappingId;
      return v != null && String(v).trim() !== "" ? String(v) : "";
    })(),
    enrollmentNo: String(o.enrollmentNo ?? o.EnrollmentNo ?? ""),
    deviceId: (() => {
      const v = o.deviceId ?? o.DeviceId;
      return v != null && String(v).trim() !== "" ? String(v) : null;
    })(),
    deviceName: String(o.deviceName ?? o.DeviceName ?? ""),
  };
};

const normalizeEmployeeOption = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const userId = String(o.userId ?? o.UserId ?? "");
  if (!userId) return null;
  return {
    userId,
    userName: String(o.userName ?? o.UserName ?? ""),
    employeeCode: String(o.employeeCode ?? o.EmployeeCode ?? ""),
  };
};

/**
 * @param {{ search?: string, status?: string, page?: number, pageSize?: number }} [opts]
 */
export const fetchMappingRoster = async (opts = {}) => {
  const page = Number(opts.page) > 0 ? Number(opts.page) : 1;
  const pageSize = Number(opts.pageSize) > 0 ? Number(opts.pageSize) : MAPPING_ROSTER_PAGE_SIZE;
  const status = String(opts.status || "unmapped").trim() || "unmapped";
  const search = String(opts.search || "").trim();
  const res = await axiosBasic.get(AttendanceDeviceAdmin.MappingRoster, {
    params: { search: search || undefined, status, page, pageSize },
  });
  assertAttendanceOk(res.data);
  const raw = unwrapEnvelope(res.data);
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? /** @type {Record<string, unknown>} */ (raw) : {};
  const items = asArray(o.items ?? o.Items ?? raw).map(normalizeMappingRosterRow).filter(Boolean);
  const unmappedEmployees = asArray(o.unmappedEmployees ?? o.UnmappedEmployees)
    .map(normalizeEmployeeOption)
    .filter(Boolean);
  return {
    items,
    totalCount: Number(o.totalCount ?? o.TotalCount ?? items.length) || items.length,
    page: Number(o.page ?? o.Page ?? page) || page,
    pageSize: Number(o.pageSize ?? o.PageSize ?? pageSize) || pageSize,
    unmappedCount: Number(o.unmappedCount ?? o.UnmappedCount ?? 0) || 0,
    mappedCount: Number(o.mappedCount ?? o.MappedCount ?? 0) || 0,
    enrollOptions: readStringList(o.enrollOptions ?? o.EnrollOptions),
    unclaimedEnrollOptions: readStringList(o.unclaimedEnrollOptions ?? o.UnclaimedEnrollOptions),
    unmappedEmployees,
  };
};

export const fetchEnrollOptions = async () => {
  const res = await axiosBasic.get(AttendanceDeviceAdmin.EnrollOptions);
  assertAttendanceOk(res.data);
  const raw = unwrapEnvelope(res.data);
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? /** @type {Record<string, unknown>} */ (raw) : {};
  return {
    enrollOptions: readStringList(o.enrollOptions ?? o.EnrollOptions ?? raw),
    unclaimedEnrollOptions: readStringList(o.unclaimedEnrollOptions ?? o.UnclaimedEnrollOptions),
  };
};

export const updateMappingEnrollment = async ({ userId, enrollmentNo, deviceId }) => {
  const res = await axiosBasic.put(AttendanceDeviceAdmin.MappingEnrollment, {
    UserId: userId,
    EnrollmentNo: enrollmentNo,
    DeviceId: deviceId || null,
  });
  assertAttendanceOk(res.data);
  return normalizeDeviceMapping(unwrapEnvelope(res.data));
};

export const saveDeviceMapping = async ({ deviceId, enrollmentNo, userId }) => {
  const res = await axiosBasic.post(AttendanceDeviceAdmin.Mappings, {
    DeviceId: deviceId || null,
    EnrollmentNo: enrollmentNo,
    UserId: userId,
  });
  assertAttendanceOk(res.data);
  return normalizeDeviceMapping(unwrapEnvelope(res.data));
};

export const deleteDeviceMapping = async (mappingId) => {
  const id = encodeURIComponent(String(mappingId || "").trim());
  if (!id) throw new Error("Mapping id is required");
  const res = await axiosBasic.delete(`${AttendanceDeviceAdmin.MappingByIdBase}${id}`);
  assertAttendanceOk(res.data);
};

const UNMAPPED_DEFAULT_PAGE_SIZE = 200;

const readPagedTotal = (envelope, fallback) => {
  const n = Number(envelope?.total ?? envelope?.Total ?? fallback);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/**
 * @param {{ page?: number, pageSize?: number }} [opts]
 * @returns {Promise<{ items: ReturnType<typeof normalizeUnmappedPunch>[], totalCount: number, page: number, pageSize: number }>}
 */
export const fetchUnmappedPunches = async (opts = {}) => {
  const page = Number(opts.page) > 0 ? Number(opts.page) : 1;
  const pageSize = Number(opts.pageSize) > 0 ? Number(opts.pageSize) : UNMAPPED_DEFAULT_PAGE_SIZE;
  const res = await axiosBasic.get(AttendanceDeviceAdmin.UnmappedPunches, { params: { page, pageSize } });
  assertAttendanceOk(res.data);
  const raw = unwrapEnvelope(res.data);
  const items = asArray(raw).map(normalizeUnmappedPunch).filter(Boolean);
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? /** @type {Record<string, unknown>} */ (raw) : {};
  const totalCount = readPagedTotal(
    res.data,
    Number(o.totalCount ?? o.TotalCount ?? items.length) || items.length
  );
  const resolvedPage = Number(o.page ?? o.Page ?? page) || page;
  const resolvedPageSize = Number(o.pageSize ?? o.PageSize ?? pageSize) || pageSize;
  return { items, totalCount, page: resolvedPage, pageSize: resolvedPageSize };
};

/**
 * @param {{ page?: number, pageSize?: number }} [opts]
 * @returns {Promise<{ items: ReturnType<typeof normalizeMappedPunch>[], totalCount: number, page: number, pageSize: number }>}
 */
export const fetchMappedPunches = async (opts = {}) => {
  const page = Number(opts.page) > 0 ? Number(opts.page) : 1;
  const pageSize = Number(opts.pageSize) > 0 ? Number(opts.pageSize) : UNMAPPED_DEFAULT_PAGE_SIZE;
  const res = await axiosBasic.get(AttendanceDeviceAdmin.MappedPunches, { params: { page, pageSize } });
  assertAttendanceOk(res.data);
  const raw = unwrapEnvelope(res.data);
  const items = asArray(raw).map(normalizeMappedPunch).filter(Boolean);
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? /** @type {Record<string, unknown>} */ (raw) : {};
  const totalCount = readPagedTotal(
    res.data,
    Number(o.totalCount ?? o.TotalCount ?? items.length) || items.length
  );
  const resolvedPage = Number(o.page ?? o.Page ?? page) || page;
  const resolvedPageSize = Number(o.pageSize ?? o.PageSize ?? pageSize) || pageSize;
  return { items, totalCount, page: resolvedPage, pageSize: resolvedPageSize };
};

export const reprocessUnmappedPunches = async () => {
  const res = await axiosBasic.post(AttendanceDeviceAdmin.ReprocessUnmapped);
  assertAttendanceOk(res.data);
  const raw = unwrapEnvelope(res.data);
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? /** @type {Record<string, unknown>} */ (raw) : {};
  return {
    applied: Number(o.applied ?? o.Applied ?? 0) || 0,
    skipped: Number(o.skipped ?? o.Skipped ?? 0) || 0,
    failed: Number(o.failed ?? o.Failed ?? 0) || 0,
    remainingUnmapped: Number(o.remainingUnmapped ?? o.RemainingUnmapped ?? 0) || 0,
    examined: Number(o.examined ?? o.Examined ?? 0) || 0,
  };
};

export { UNMAPPED_DEFAULT_PAGE_SIZE, MAPPING_ROSTER_PAGE_SIZE, CUSTOM_ENROLL };

export { handleAttendanceApiError };
